/**
 * entradas-runtime.js — El widget de VENTA DE ENTRADAS (día → sesión → entradas → datos → pago).
 *
 * UN SOLO WIDGET, DOS FORMAS DE MONTARSE. Todo elemento `[data-n3i-entradas="<id del evento>"]`
 * es un widget, venga de:
 *   · el bloque `entradas` del builder (lo pinta su render dentro de su `<section>`), o
 *   · una página HTML hecha a mano, con el tag `<div data-n3i-entradas="7"></div>`
 *     (`web/lib/entradas_tag.py` convierte también los `<ticket_booking_form>` antiguos).
 * El contenedor llega VACÍO y este módulo lo construye entero desde la config del evento, así que
 * las dos formas se ven y se comportan igual. El estilo es el del builder (`.ne-*` en
 * `css/site.css`, con los tokens de la sección: `--accent`, `--text`, `--card-*`…); una página a
 * mano lo retoca con su propio CSS.
 *
 * POR QUÉ ES UN MÓDULO Y NO on* INLINE (gotcha #13): el saneo del HTML publicado borra los on*.
 * Toda la interacción va por DELEGACIÓN DE CLASE (`.ne-*`) con unos pocos listeners en `document`,
 * como `reserva-live-runtime.js`. Lo arranca `core/block-actions.js` (los dos ENTRY lo cargan).
 *
 * EN EL EDITOR (`body.editor-active`) el widget se pinta con datos reales —el dueño ve su
 * calendario y sus precios— pero COMPRAR queda INERTE, con un sello de vista previa.
 *
 * ══ CONTRATO (SSOT = el docstring de web/views_entradas.py) ══
 *  GET  /site_entradas/evento/?id=N&lang=xx → {ok, evento:{id, nombre, aforo, minimo, por_bloques,
 *        pago_online, dias_semana (lunes 0), cierres[[desde,hasta]], horas[{valor,etiqueta}], hoy,
 *        max_dias, max_por_tipo, tipos[{product_id,nombre,precio,cupo,vinculado|null}],
 *        medios[{id,metodo,titulo,logos[{clave,nombre,url}],propio}]}, etiquetas:{ne_*}}
 *  GET  /site_entradas/sesiones/?id=N&fecha=AAAA-MM-DD → {ok, fecha, sesiones[{valor,etiqueta,libres|null}]}
 *  POST /site_entradas/agente/  (JSON: codigo) → {ok, codigo, pago_opcional} | {ok:false, error:'agente_no_valido'}
 *  POST /site_entradas/comprar/ (JSON: id, fecha, hora, items[{id,cantidad}], nombre, telefono,
 *        email, pagina, _hp, _idem, lang, medio, agente, pagar_luego) → {ok, reserva:{codigo,fecha,hora,evento,
 *        sin_pagar?}} | {ok, pago:{url,total}} | {ok:false, error}
 *  GET  /medios_cobro/estado/<tx>/   → {ok, estado, materializada, resultado:{reserva}} (web/medios_cobro)
 *
 * LA VUELTA DEL BANCO: el pago se hace fuera (página de pago de la plataforma de cobro → Redsys) y el
 * visitante vuelve a ESTA página con `?n3i_tx=<uuid>`. El aviso del banco puede llegar unos segundos
 * después que él, así que mientras la transacción siga en el banco (o creándose la venta) se pregunta otra vez.
 *
 * CÓMO PAGAR: con más de un medio de cobro para entradas en este sitio (Redsys, PayPal, Stripe…), el paso de los
 * datos enseña «¿Cómo quieres pagar?» con una tarjeta por medio, justo encima del botón de pagar; con uno solo, sus
 * logos bajo la nota. Lo elegido viaja como `medio` y el servidor lo vuelve a comprobar (`medios_disponibles`).
 *
 * CÓDIGO DE AGENTE (punto 5 del plan de entradas): si la empresa tiene agentes comerciales con código
 * (`evento.agentes`), los datos ofrecen «¿Tienes un código de agente?». Validado (`/site_entradas/agente/`), la
 * compra viaja con `agente` y, en un evento de pago y si el agente tiene el pago opcional (`pago_opcional`), el
 * comprador elige «Pagar ahora» (al banco, como siempre) o «Pagar más tarde» (`pagar_luego`: se reserva sin pagar).
 * Sin pago opcional, con código también se paga. El servidor lo vuelve a comprobar todo al comprar.
 *
 * Las etiquetas: el sitio publicado NO carga `NUBE3I_UI_LABELS` (lo sabe `languages.py`), así que
 * el servidor devuelve las `ne_*` del idioma de la página junto con la config del evento; mientras
 * no llegan (o en el editor) sale `t()`, con su respaldo en español.
 */
import { t } from '../utils/i18n.js';
import { escapeHtml } from '../utils/dom.js';
import { basePublica, baseApiConGemela } from '../utils/base-publica.js';
import {
    celdasDelMes, mesNavegable, primerDiaVendible, diaVendible, lineasDe, totalCentimos, plazasDe,
    itemsDe, ajustarVinculados, contactoValido, leerVueltaPago, quitarVueltaPago, veredictoPago, claveDeError,
    cfgDeSesion, cantidadPermitida,
} from '../utils/entradas.js';

/** La config con los precios de la sesión elegida (lo que se enseña, se suma y se manda a cobrar). */
function cfgVenta(st) {
    return cfgDeSesion(st.cfg, st.sesiones.find((s) => s.valor === st.hora));
}

export const ENTRADAS_ENDPOINTS = {
    evento: '/site_entradas/evento/',
    sesiones: '/site_entradas/sesiones/',
    comprar: '/site_entradas/comprar/',
    agente: '/site_entradas/agente/',
    estado: '/medios_cobro/estado/',
};

const SELECTOR = '[data-n3i-entradas]';
const PASOS = ['dia', 'sesion', 'entradas', 'datos', 'hecho'];
const MS_CONSULTA = 12000;
const MS_COMPRA = 30000;
// Sondeo de la vuelta de Redsys: cada 2 s durante 40 s. Después se dice la verdad («estamos
// esperando al banco») en vez de dar el pago por fallido: puede estar cobrado.
const SONDEO_MS = 2000;
const SONDEO_INTENTOS = 20;
// Recuerda qué evento se estaba pagando, para que a la vuelta conteste el widget correcto si la
// página tiene varios. sessionStorage: es de esta pestaña y de este viaje a la pasarela.
const CLAVE_PAGO = 'n3i_entradas_pago';

const _estados = new WeakMap();

// Método del medio (MedioCobro.metodo) → su nombre para el comprador, en el idioma de la página. Uno desconocido,
// con el título que manda el servidor (en español).
const ETIQUETA_METODO = {
    tarjeta: 'ne_m_tarjeta', solo_tarjeta: 'ne_m_tarjeta', bizum: 'ne_m_bizum', wallet: 'ne_m_wallet', paypal: 'ne_m_paypal',
};

function esEditor() {
    return typeof document !== 'undefined' && document.body.classList.contains('editor-active');
}

function langDe(el) {
    const conLang = el.closest('[lang]');
    return ((conLang && conLang.getAttribute('lang')) || document.documentElement.lang || 'es')
        .toLowerCase().slice(0, 5);
}

/** Etiqueta en el idioma de la página: la que mandó el servidor, o `t()`. */
function e(st, clave, vars) {
    const base = (st.etiquetas && st.etiquetas[clave] != null) ? st.etiquetas[clave] : t(clave);
    if (!vars) return base;
    return String(base).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : String(vars[k])));
}

/** Como `e()` pero en singular (`<clave>_one`) cuando n es 1. */
function en(st, clave, n, vars) {
    return e(st, n === 1 ? clave + '_one' : clave, Object.assign({ n }, vars || {}));
}

function precio(st, centimos) {
    if (!centimos) return e(st, 'ne_free');
    try {
        return new Intl.NumberFormat(st.lang, { style: 'currency', currency: 'EUR' }).format(centimos / 100);
    } catch (_) {
        return (centimos / 100).toFixed(2).replace('.', ',') + ' €';
    }
}

/** Primera letra en mayúscula y el resto como lo da Intl. `text-transform: capitalize` no sirve:
 *  pone en mayúscula cada palabra («Domingo, 18 De Octubre»). */
function mayuscula(txt) {
    const s = String(txt || '');
    return s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : s;
}

function fechaLarga(st, iso) {
    const p = String(iso || '').split('-').map(Number);
    if (p.length !== 3) return iso || '';
    try {
        return mayuscula(new Intl.DateTimeFormat(st.lang, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
            .format(new Date(Date.UTC(p[0], p[1] - 1, p[2]))));
    } catch (_) {
        return iso;
    }
}

function nuevaIdem() {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch (_) { /* contexto no seguro: http en desarrollo */ }
    return 'ne-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// ── Red ──────────────────────────────────────────────────────────────────────
/**
 * fetch con tope de tiempo. Devuelve el JSON (también el de un 4xx: ahí viene `error`) o null ante
 * un fallo de red, un timeout o una respuesta que no es JSON. Nunca lanza.
 */
async function pedir(url, opciones, ms) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const to = setTimeout(() => { if (ctrl) ctrl.abort(); }, ms);
    try {
        const res = await fetch(url, Object.assign({ signal: ctrl ? ctrl.signal : undefined, credentials: 'same-origin' }, opciones || {}));
        const tipo = res.headers.get('content-type') || '';
        if (res.redirected || tipo.indexOf('json') === -1) return null;
        return await res.json();
    } catch (_) {
        return null;
    } finally {
        clearTimeout(to);
    }
}

// Las consultas (evento, sesiones) tienen gemela en `/{tenant}/builder/site_entradas/…` (web/admin.py): en el
// lienzo del editor el tenant va en la RUTA y la pública prefijada no casa con nada. En lo publicado las dos
// bases dan '' y es la misma URL de siempre. Comprar y estado no la necesitan: en el editor no se compra.
const CON_GEMELA = [ENTRADAS_ENDPOINTS.evento, ENTRADAS_ENDPOINTS.sesiones];

function url(st, ruta, params) {
    const q = new URLSearchParams(params || {}).toString();
    const base = CON_GEMELA.indexOf(ruta) !== -1 ? baseApiConGemela() : basePublica();
    return base + ruta + (q ? '?' + q : '');
}

// ── Estado ───────────────────────────────────────────────────────────────────
function estadoDe(widget) {
    let st = _estados.get(widget);
    if (!st) {
        st = {
            id: String(widget.getAttribute('data-n3i-entradas') || '').trim(),
            lang: langDe(widget),
            cfg: null,
            etiquetas: null,
            paso: 'dia',
            mes: null,                // [año, mes 0-11] que enseña el calendario
            fecha: '',
            sesiones: [],
            hora: '',
            cantidades: {},
            idem: '',
            medio: '',                // id del medio de cobro elegido ('' = el primero, como siempre)
            agente: '',               // código de agente ya validado ('' = sin agente)
            pagoOpcional: false,      // ese agente puede reservar sin pagar
            agenteAbierto: false,     // se ve la casilla del código
            validandoAgente: false,
            pagarLuego: false,        // con agente, en un evento de pago: reservar sin pagar
            enviando: false,
            sondeo: 0,
        };
        _estados.set(widget, st);
    }
    return st;
}

function anunciar(widget, msg, error) {
    const live = widget.querySelector('.ne-aviso');
    if (!live) return;
    live.textContent = msg || '';
    live.hidden = !msg;
    live.classList.toggle('ne-aviso-error', !!(msg && error));
}

// ── Pintado ──────────────────────────────────────────────────────────────────
/** Esqueleto del widget: lo construye entero (el contenedor llega vacío, en bloque y en tag). */
function pintarEsqueleto(widget, st) {
    const nombre = st.cfg ? st.cfg.nombre : '';
    widget.classList.add('ne-entradas');
    widget.innerHTML = `
        <div class="ne-cabeza">
            ${nombre ? `<p class="ne-evento">${escapeHtml(nombre)}</p>` : ''}
            <ol class="ne-pasos" aria-hidden="true">
                ${['dia', 'sesion', 'entradas', 'datos'].map((p, i) => `
                    <li class="ne-paso-pill" data-paso-pill="${p}"><span>${i + 1}</span>${escapeHtml(e(st, 'ne_step_' + p))}</li>`).join('')}
            </ol>
        </div>
        <p class="ne-eleccion" hidden></p>
        <div class="ne-panel" data-paso="dia">
            <h3 class="ne-titulo">${escapeHtml(e(st, 'ne_pick_day'))}</h3>
            <div class="ne-cal"></div>
        </div>
        <div class="ne-panel" data-paso="sesion" hidden>
            <h3 class="ne-titulo">${escapeHtml(e(st, 'ne_pick_session'))}</h3>
            <div class="ne-sesiones" role="radiogroup"></div>
        </div>
        <div class="ne-panel" data-paso="entradas" hidden>
            <h3 class="ne-titulo">${escapeHtml(e(st, 'ne_pick_tickets'))}</h3>
            <div class="ne-tipos"></div>
            <p class="ne-nota" hidden></p>
        </div>
        <div class="ne-panel" data-paso="datos" hidden>
            <h3 class="ne-titulo">${escapeHtml(e(st, 'ne_contact'))}</h3>
            <div class="ne-campos">
                <label class="ne-campo"><span>${escapeHtml(e(st, 'ne_name'))}</span>
                    <input type="text" class="ne-input" name="nombre" autocomplete="name" maxlength="100" required></label>
                <label class="ne-campo"><span>${escapeHtml(e(st, 'ne_phone'))}</span>
                    <input type="tel" class="ne-input" name="telefono" autocomplete="tel" maxlength="100" required></label>
                <label class="ne-campo"><span>${escapeHtml(e(st, 'ne_email'))}</span>
                    <input type="email" class="ne-input" name="email" autocomplete="email" maxlength="255" required></label>
                <label class="ne-hp" aria-hidden="true">Web<input type="text" name="_hp" tabindex="-1" autocomplete="off"></label>
            </div>
            <div class="ne-agente" hidden></div>
            <div class="ne-resumen"></div>
            <div class="ne-pago" hidden></div>
        </div>
        <div class="ne-panel ne-hecho" data-paso="hecho" tabindex="-1" hidden></div>
        <p class="ne-aviso" role="status" aria-live="polite" hidden></p>
        <div class="ne-acciones">
            <button type="button" class="ne-btn ne-btn-sec ne-volver" hidden>${escapeHtml(e(st, 'ne_back'))}</button>
            <button type="button" class="ne-btn ne-seguir" hidden>${escapeHtml(e(st, 'ne_next'))}</button>
            <button type="button" class="ne-btn ne-comprar" hidden></button>
        </div>
        ${esEditor() ? `<p class="ne-sello editor-only">${escapeHtml(e(st, 'ne_preview'))}</p>` : ''}`;
}

function pintarCalendario(widget, st) {
    const caja = widget.querySelector('.ne-cal');
    if (!caja || !st.cfg) return;
    const [anio, mes] = st.mes;
    const fmtMes = (() => {
        try { return new Intl.DateTimeFormat(st.lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }); } catch (_) { return null; }
    })();
    const titulo = fmtMes ? mayuscula(fmtMes.format(new Date(Date.UTC(anio, mes, 1)))) : `${mes + 1}/${anio}`;
    const fmtDia = (() => {
        try { return new Intl.DateTimeFormat(st.lang, { weekday: 'short', timeZone: 'UTC' }); } catch (_) { return null; }
    })();
    // 2024-01-01 fue lunes: de ahí salen los nombres de la semana en el idioma de la página.
    const cabecera = [0, 1, 2, 3, 4, 5, 6].map((i) => (fmtDia ? fmtDia.format(new Date(Date.UTC(2024, 0, 1 + i))) : ''));
    const prev = mes === 0 ? [anio - 1, 11] : [anio, mes - 1];
    const next = mes === 11 ? [anio + 1, 0] : [anio, mes + 1];
    const celdas = celdasDelMes(anio, mes, st.cfg);
    caja.innerHTML = `
        <div class="ne-cal-cab">
            <button type="button" class="ne-cal-nav" data-mes="${prev[0]}-${prev[1]}" aria-label="${escapeHtml(e(st, 'ne_prev_month'))}" ${mesNavegable(prev[0], prev[1], st.cfg) ? '' : 'disabled'}>‹</button>
            <span class="ne-cal-mes">${escapeHtml(titulo)}</span>
            <button type="button" class="ne-cal-nav" data-mes="${next[0]}-${next[1]}" aria-label="${escapeHtml(e(st, 'ne_next_month'))}" ${mesNavegable(next[0], next[1], st.cfg) ? '' : 'disabled'}>›</button>
        </div>
        <div class="ne-cal-grid">
            ${cabecera.map((d) => `<span class="ne-cal-sem">${escapeHtml(d)}</span>`).join('')}
            ${celdas.map((c) => (c.iso === null ? '<span></span>' : `
                <button type="button" class="ne-dia${c.iso === st.fecha ? ' is-active' : ''}${c.hoy ? ' is-hoy' : ''}"
                    data-fecha="${c.iso}" ${c.vendible ? '' : 'disabled'} aria-pressed="${c.iso === st.fecha}">${c.dia}</button>`)).join('')}
        </div>`;
}

function pintarSesiones(widget, st) {
    const caja = widget.querySelector('.ne-sesiones');
    if (!caja) return;
    if (!st.sesiones.length) {
        caja.innerHTML = `<p class="ne-vacio">${escapeHtml(e(st, 'ne_no_sessions'))}</p>`;
        return;
    }
    caja.innerHTML = st.sesiones.map((s) => {
        const libres = (typeof s.libres === 'number') ? s.libres : null;
        const lleno = libres === 0;
        const extra = libres === null ? '' : (lleno ? e(st, 'ne_full') : en(st, 'ne_left', libres));
        return `
            <button type="button" class="ne-sesion${s.valor === st.hora ? ' is-active' : ''}" role="radio"
                aria-checked="${s.valor === st.hora}" data-hora="${escapeHtml(s.valor)}" ${lleno ? 'disabled' : ''}>
                <span class="ne-sesion-hora">${escapeHtml(s.etiqueta || s.valor)}</span>
                ${extra ? `<span class="ne-sesion-libres">${escapeHtml(extra)}</span>` : ''}
            </button>`;
    }).join('');
}

function filaTipo(st, tipo, vinculado) {
    const n = Math.trunc(Number(st.cantidades[tipo.product_id]) || 0);
    const puedeMas = cantidadPermitida(st.cfg, st.cantidades, tipo.product_id, n + 1) > n;
    const cent = Math.round(Number(tipo.precio) * 100);
    return `
        <div class="ne-tipo${vinculado ? ' ne-tipo-vinculado' : ''}">
            <div class="ne-tipo-info">
                <span class="ne-tipo-nombre">${vinculado ? '+ ' : ''}${escapeHtml(tipo.nombre)}</span>
                <span class="ne-tipo-precio">${escapeHtml(precio(st, cent))}</span>
            </div>
            <div class="ne-contador" data-producto="${tipo.product_id}">
                <button type="button" class="ne-menos" aria-label="${escapeHtml(e(st, 'ne_less', { x: tipo.nombre }))}" ${n > 0 ? '' : 'disabled'}>−</button>
                <span class="ne-cantidad" aria-live="polite">${n}</span>
                <button type="button" class="ne-mas" aria-label="${escapeHtml(e(st, 'ne_more', { x: tipo.nombre }))}" ${puedeMas ? '' : 'disabled'}>+</button>
            </div>
        </div>`;
}

function pintarTipos(widget, st) {
    const caja = widget.querySelector('.ne-tipos');
    if (!caja || !st.cfg) return;
    caja.innerHTML = cfgVenta(st).tipos.map((tipo) => {
        let html = filaTipo(st, tipo, false);
        // El vinculado es el «suplemento» de una entrada: solo aparece con su principal elegido.
        if (tipo.vinculado && Number(st.cantidades[tipo.product_id]) > 0) html += filaTipo(st, tipo.vinculado, true);
        return html;
    }).join('') || `<p class="ne-vacio">${escapeHtml(e(st, 'ne_no_tickets'))}</p>`;
    pintarNota(widget, st);
}

/** Lo que bloquea la selección frente a lo que queda en la sesión, y el mínimo del evento. */
function comprobarSeleccion(st) {
    const lineas = lineasDe(cfgVenta(st), st.cantidades);
    const { plazas, bloqueadas } = plazasDe(st.cfg, lineas);
    const sesion = st.sesiones.find((s) => s.valor === st.hora);
    const libres = sesion && typeof sesion.libres === 'number' ? sesion.libres : null;
    return { lineas, plazas, bloqueadas, libres, cabe: libres === null || bloqueadas <= libres };
}

function pintarNota(widget, st) {
    const nota = widget.querySelector('.ne-nota');
    if (!nota) return;
    const c = comprobarSeleccion(st);
    let msg = '';
    if (c.lineas.length && !c.cabe) msg = en(st, 'ne_not_enough', c.libres);
    else if (Number(st.cfg.minimo) > 1) msg = e(st, 'ne_min', { n: st.cfg.minimo });
    else if (Number(st.cfg.max_total) > 0) msg = e(st, 'ne_max_total', { n: st.cfg.max_total });
    nota.textContent = msg;
    nota.hidden = !msg;
    nota.classList.toggle('ne-nota-error', !!(c.lineas.length && !c.cabe));
}

function pintarResumen(widget, st) {
    const caja = widget.querySelector('.ne-resumen');
    if (!caja) return;
    const { lineas } = comprobarSeleccion(st);
    const total = totalCentimos(lineas);
    const luego = pagaLuego(st, total);
    const sesion = st.sesiones.find((s) => s.valor === st.hora);
    caja.innerHTML = `
        <p class="ne-resumen-cuando">${escapeHtml(fechaLarga(st, st.fecha))} · ${escapeHtml(sesion ? (sesion.etiqueta || sesion.valor) : st.hora)}</p>
        <ul class="ne-resumen-lineas">
            ${lineas.map((l) => `<li><span>${l.cantidad} × ${escapeHtml(l.nombre)}</span><span>${escapeHtml(precio(st, Math.round(Number(l.precio) * 100) * l.cantidad))}</span></li>`).join('')}
        </ul>
        <p class="ne-resumen-total"><span>${escapeHtml(e(st, 'ne_total'))}</span><strong>${escapeHtml(precio(st, total))}</strong></p>
        ${st.cfg.pago_online && total > 0 ? `<p class="ne-resumen-nota">${escapeHtml(luego ? e(st, 'ne_pay_later_note') : notaDePago(st))}</p>` : ''}`;
    const boton = widget.querySelector('.ne-comprar');
    if (boton) {
        boton.textContent = !(st.cfg.pago_online && total > 0) ? e(st, 'ne_book')
            : (luego ? e(st, 'ne_book_later') : e(st, 'ne_pay', { total: precio(st, total) }));
    }
    pintarPago(widget, st, !!(st.cfg.pago_online && total > 0) && !luego);
    pintarAgente(widget, st, !!(st.pagoOpcional && st.cfg.pago_online && total > 0));
}

// ── Código de agente ─────────────────────────────────────────────────────────
/** Con agente validado con pago opcional y algo que cobrar online, el comprador puede dejarlo sin pagar. */
function pagaLuego(st, total) {
    return !!(st.agente && st.pagoOpcional && st.pagarLuego && st.cfg.pago_online && total > 0);
}

const ICONO_OK = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5 10 17.5 19 7"/></svg>';

/** El enlace, la casilla del código o el código aplicado y, con algo que cobrar, «¿Cuándo quieres pagar?». */
function pintarAgente(widget, st, pagable) {
    const caja = widget.querySelector('.ne-agente');
    if (!caja) return;
    if (!st.cfg || !st.cfg.agentes) { caja.hidden = true; caja.innerHTML = ''; return; }
    caja.hidden = false;
    if (!st.agente) {
        // Se conserva lo que estuviera escrito (repintar no puede borrar lo que el comprador ha tecleado).
        const previo = widget.querySelector('.ne-agente-input');
        const valor = previo ? previo.value : '';
        caja.innerHTML = !st.agenteAbierto
            ? `<button type="button" class="ne-enlace ne-agente-abrir">${escapeHtml(e(st, 'ne_agent_link'))}</button>`
            : `<label class="ne-campo"><span>${escapeHtml(e(st, 'ne_agent_code'))}</span>
                <span class="ne-agente-fila">
                    <input type="text" class="ne-input ne-agente-input" name="agente" autocomplete="off" autocapitalize="characters"
                        spellcheck="false" maxlength="40" value="${escapeHtml(valor)}">
                    <button type="button" class="ne-btn ne-btn-sec ne-agente-aplicar"${st.validandoAgente ? ' disabled' : ''}>${escapeHtml(e(st, 'ne_agent_apply'))}</button>
                </span></label>`;
        return;
    }
    const opcion = (valor, clave) => {
        const si = (valor === 'luego') === !!st.pagarLuego;
        return `
            <button type="button" class="ne-cuando-opcion${si ? ' is-active' : ''}" role="radio" aria-checked="${si}"
                tabindex="${si ? '0' : '-1'}" data-cuando="${valor}">
                <span class="ne-medio-marca" aria-hidden="true"></span>
                <span class="ne-medio-nombre">${escapeHtml(e(st, clave))}</span>
            </button>`;
    };
    caja.innerHTML = `
        <p class="ne-agente-ok">${ICONO_OK}<span>${escapeHtml(e(st, 'ne_agent_ok'))} · <strong>${escapeHtml(st.agente)}</strong></span>
            <button type="button" class="ne-enlace ne-agente-quitar">${escapeHtml(e(st, 'ne_agent_remove'))}</button></p>
        ${pagable ? `
        <p class="ne-pago-titulo" aria-hidden="true">${escapeHtml(e(st, 'ne_when_pay'))}</p>
        <div class="ne-cuando" role="radiogroup" aria-label="${escapeHtml(e(st, 'ne_when_pay'))}">
            ${opcion('ahora', 'ne_pay_now')}${opcion('luego', 'ne_pay_later')}
        </div>` : ''}`;
}

async function aplicarAgente(widget, st) {
    if (esEditor() || st.validandoAgente) return;
    const input = widget.querySelector('.ne-agente-input');
    const codigo = input ? String(input.value || '').trim() : '';
    if (!codigo) { if (input) input.focus(); return; }
    st.validandoAgente = true;
    pintarResumen(widget, st);
    anunciar(widget, e(st, 'ne_loading'));
    let r;
    try {
        r = await pedir(url(st, ENTRADAS_ENDPOINTS.agente), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ codigo }),
        }, MS_CONSULTA);
    } finally {
        st.validandoAgente = false;
    }
    if (r && r.ok && r.codigo) {
        st.agente = String(r.codigo);
        st.pagoOpcional = r.pago_opcional === true;
        st.pagarLuego = false;
        st.idem = '';                          // con agente es otra compra
        anunciar(widget, '');
        pintarResumen(widget, st);
        return;
    }
    pintarResumen(widget, st);
    anunciar(widget, e(st, claveDeError(r && r.error)), true);
    const otra = widget.querySelector('.ne-agente-input');
    if (otra) otra.focus();
}

function quitarAgente(widget, st) {
    st.agente = '';
    st.pagoOpcional = false;
    st.pagarLuego = false;
    st.agenteAbierto = false;
    st.idem = '';
    anunciar(widget, '');
    pintarResumen(widget, st);
}

function elegirCuando(widget, st, valor) {
    const luego = valor === 'luego';
    if (luego === st.pagarLuego) return;
    st.pagarLuego = luego;
    st.idem = '';                              // pagar luego o ahora = otra compra (sin transacción o con ella)
    pintarResumen(widget, st);
    const activo = widget.querySelector('.ne-cuando-opcion.is-active');
    if (activo) activo.focus();
}

// ── Cómo pagar ───────────────────────────────────────────────────────────────
function mediosDe(st) {
    return (st.cfg && Array.isArray(st.cfg.medios)) ? st.cfg.medios.filter((m) => m && m.id != null) : [];
}

function medioElegido(st) {
    const medios = mediosDe(st);
    return medios.find((m) => String(m.id) === String(st.medio)) || medios[0] || null;
}

function nombreDeMedio(st, m) {
    if (m.propio) return m.propio;           // dos medios con el mismo método: el nombre que les dio la empresa
    const clave = ETIQUETA_METODO[m.metodo];
    return clave ? e(st, clave) : (m.titulo || '');
}

/** La nota bajo el total: PayPal no es «la pasarela del banco». */
function notaDePago(st) {
    const m = medioElegido(st);
    return e(st, m && m.metodo === 'paypal' ? 'ne_pay_note_paypal' : 'ne_pay_note');
}

function logosHtml(m) {
    return (Array.isArray(m.logos) ? m.logos : []).map((l) => `<img src="${escapeHtml(l.url || '')}" alt="${escapeHtml(l.nombre || '')}" title="${escapeHtml(l.nombre || '')}" height="22" loading="lazy" decoding="async">`).join('');
}

/** Con varios medios, una tarjeta por medio (radio); con uno, solo sus logos; sin pago o sin lista, nada. */
function pintarPago(widget, st, pagar) {
    const caja = widget.querySelector('.ne-pago');
    if (!caja) return;
    const medios = mediosDe(st);
    if (!pagar || !medios.length) { caja.hidden = true; caja.innerHTML = ''; return; }
    caja.hidden = false;
    if (medios.length === 1) {
        caja.innerHTML = `<div class="ne-pago-logos">${logosHtml(medios[0])}</div>`;
        return;
    }
    const activo = medioElegido(st);
    caja.innerHTML = `
        <p class="ne-pago-titulo" aria-hidden="true">${escapeHtml(e(st, 'ne_how_pay'))}</p>
        <div class="ne-medios" role="radiogroup" aria-label="${escapeHtml(e(st, 'ne_how_pay'))}">
            ${medios.map((m) => {
                const si = m === activo;
                return `
                <button type="button" class="ne-medio${si ? ' is-active' : ''}" role="radio" aria-checked="${si}"
                    tabindex="${si ? '0' : '-1'}" data-medio="${escapeHtml(String(m.id))}">
                    <span class="ne-medio-marca" aria-hidden="true"></span>
                    <span class="ne-medio-nombre">${escapeHtml(nombreDeMedio(st, m))}</span>
                    <span class="ne-medio-logos">${logosHtml(m)}</span>
                </button>`;
            }).join('')}
        </div>`;
}

/** Cambia de medio SIN repintar las tarjetas (el foco se queda donde estaba) y pone al día la nota. */
function elegirMedio(widget, st, id, enfocar) {
    if (!mediosDe(st).some((m) => String(m.id) === String(id))) return;
    if (String(st.medio) !== String(id)) st.idem = '';   // otro medio = otra compra (otra transacción)
    st.medio = String(id);
    widget.querySelectorAll('.ne-medio').forEach((b) => {
        const si = b.getAttribute('data-medio') === String(id);
        b.classList.toggle('is-active', si);
        b.setAttribute('aria-checked', String(si));
        b.setAttribute('tabindex', si ? '0' : '-1');
        if (si && enfocar) b.focus();
    });
    const nota = widget.querySelector('.ne-resumen-nota');
    if (nota) nota.textContent = notaDePago(st);
}

/** «Sábado 18 de octubre · 11:00» encima de los pasos, para no perder de vista lo elegido. */
function pintarEleccion(widget, st) {
    const el = widget.querySelector('.ne-eleccion');
    if (!el) return;
    const partes = [];
    if (st.fecha && st.paso !== 'dia' && st.paso !== 'hecho') partes.push(fechaLarga(st, st.fecha));
    const sesion = st.sesiones.find((s) => s.valor === st.hora);
    if (sesion && (st.paso === 'entradas' || st.paso === 'datos')) partes.push(sesion.etiqueta || sesion.valor);
    if (st.paso === 'datos') partes.length = 0;     // en «Datos» ya lo dice el resumen de la compra
    el.textContent = partes.join(' · ');
    el.hidden = !partes.length;
}

// Insignias del resultado (SVG, sin emojis). El check y la X se DIBUJAN (stroke-dashoffset en site.css).
const INSIGNIA = {
    ok: '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true"><path class="ne-res-trazo" pathLength="1" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5 10 17.5 19 7"/></svg>',
    error: '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path class="ne-res-trazo" pathLength="1" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" d="M7 7l10 10M17 7 7 17"/></svg>',
    pendiente: '<svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2"/><path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" d="M12 8v4.5l3 1.8"/></svg>',
    esperando: '<span class="ne-girando"></span>',
};

/**
 * La pantalla final del widget. «ok» es un billete (evento, fecha y sesión arriba; el código abajo, tras
 * el troquel); los demás, la insignia de su color con su frase. Mismas claves de texto de siempre.
 */
function pintarHecho(widget, st, reserva, modo) {
    const caja = widget.querySelector('.ne-hecho');
    if (!caja) return;
    if (modo === 'pendiente' || modo === 'error' || modo === 'esperando') {
        const clave = { pendiente: 'ne_pay_pending', error: 'ne_pay_error', esperando: 'ne_paying' }[modo];
        const boton = modo === 'esperando' ? ''
            : `<button type="button" class="ne-btn ${modo === 'error' ? '' : 'ne-btn-sec '}ne-otra">${escapeHtml(e(st, modo === 'error' ? 'ne_retry' : 'ne_again'))}</button>`;
        caja.innerHTML = `
            <div class="ne-res ne-res--${modo}">
                <div class="ne-res-insignia">${INSIGNIA[modo]}</div>
                <p class="ne-res-titulo">${escapeHtml(e(st, clave))}</p>
                ${boton}
            </div>`;
        return;
    }
    const r = reserva || {};
    const dato = (etq, valor) => valor
        ? `<div class="ne-billete-dato"><span class="ne-billete-etq">${escapeHtml(e(st, etq))}</span><strong>${escapeHtml(valor)}</strong></div>` : '';
    caja.innerHTML = `
        <div class="ne-res ne-res--ok">
            <div class="ne-res-insignia">${INSIGNIA.ok}</div>
            <p class="ne-res-titulo">${escapeHtml(e(st, 'ne_done'))}</p>
            <p class="ne-res-sub">${escapeHtml(e(st, 'ne_email_sent'))}${r.sin_pagar ? ' ' + escapeHtml(e(st, 'ne_pay_later_done')) : ''}</p>
            <div class="ne-billete">
                <div class="ne-billete-cuerpo">
                    ${r.evento ? `<span class="ne-billete-etq">${escapeHtml(e(st, 'ne_event'))}</span><p class="ne-billete-evento">${escapeHtml(r.evento)}</p>` : ''}
                    <div class="ne-billete-fila">
                        ${dato('ne_date', r.fecha ? fechaLarga(st, r.fecha) : '')}
                        ${dato('ne_session', r.hora)}
                    </div>
                </div>
                ${r.codigo ? `
                <div class="ne-billete-corte" aria-hidden="true"></div>
                <div class="ne-billete-pie">
                    <span class="ne-billete-etq">${escapeHtml(e(st, 'ne_code'))}</span>
                    <span class="ne-codigo">${escapeHtml(r.codigo)}</span>
                </div>` : ''}
            </div>
            <button type="button" class="ne-btn ne-btn-sec ne-otra">${escapeHtml(e(st, 'ne_again'))}</button>
        </div>`;
}

function setPaso(widget, st, paso) {
    st.paso = PASOS.indexOf(paso) === -1 ? 'dia' : paso;
    widget.setAttribute('data-ne-paso', st.paso);   // el CSS esconde la cabeza en el resultado
    widget.querySelectorAll('.ne-panel').forEach((p) => { p.hidden = p.getAttribute('data-paso') !== st.paso; });
    widget.querySelectorAll('.ne-paso-pill').forEach((p) => {
        const k = p.getAttribute('data-paso-pill');
        p.classList.toggle('is-active', k === st.paso);
        p.classList.toggle('is-done', PASOS.indexOf(k) < PASOS.indexOf(st.paso));
    });
    const volver = widget.querySelector('.ne-volver');
    const seguir = widget.querySelector('.ne-seguir');
    const comprar = widget.querySelector('.ne-comprar');
    if (volver) volver.hidden = (st.paso === 'dia' || st.paso === 'hecho');
    if (seguir) {
        seguir.hidden = st.paso !== 'entradas';
        const c = comprobarSeleccion(st);
        seguir.disabled = !(c.lineas.length && c.cabe);
        // El total en vivo, en el mismo botón: antes no se veía hasta el paso de los datos.
        const total = totalCentimos(c.lineas);
        seguir.textContent = c.lineas.length ? e(st, 'ne_next') + ' · ' + precio(st, total) : e(st, 'ne_next');
    }
    if (comprar) {
        comprar.hidden = st.paso !== 'datos';
        comprar.disabled = esEditor() || st.enviando;
    }
    const pasos = widget.querySelector('.ne-pasos');
    if (pasos) pasos.hidden = st.paso === 'hecho';
    pintarEleccion(widget, st);
    if (st.paso === 'datos') pintarResumen(widget, st);
}

// ── Flujo ────────────────────────────────────────────────────────────────────
async function cargarEvento(widget, st) {
    if (!/^\d+$/.test(st.id)) {
        widget.classList.add('ne-entradas');
        widget.innerHTML = `<p class="ne-vacio">${escapeHtml(e(st, esEditor() ? 'ne_no_event' : 'ne_err_evento'))}</p>`;
        return false;
    }
    widget.classList.add('ne-entradas', 'ne-cargando');
    if (!widget.innerHTML.trim()) widget.innerHTML = `<p class="ne-vacio">${escapeHtml(e(st, 'ne_loading'))}</p>`;
    const r = await pedir(url(st, ENTRADAS_ENDPOINTS.evento, { id: st.id, lang: st.lang }), null, MS_CONSULTA);
    widget.classList.remove('ne-cargando');
    if (r && r.etiquetas) st.etiquetas = r.etiquetas;
    if (!r || !r.ok || !r.evento) {
        widget.innerHTML = `<p class="ne-vacio">${escapeHtml(e(st, r && r.error ? claveDeError(r.error) : 'ne_err_red'))}</p>`;
        return false;
    }
    st.cfg = r.evento;
    const m0 = mediosDe(st)[0];
    st.medio = m0 ? String(m0.id) : '';
    const primero = primerDiaVendible(st.cfg) || st.cfg.hoy;
    const p = primero.split('-').map(Number);
    st.mes = [p[0], p[1] - 1];
    pintarEsqueleto(widget, st);
    pintarCalendario(widget, st);
    if (!primerDiaVendible(st.cfg)) anunciar(widget, e(st, 'ne_no_days'), true);
    setPaso(widget, st, 'dia');
    return true;
}

async function elegirDia(widget, st, fecha) {
    if (!diaVendible(fecha, st.cfg)) return;
    st.fecha = fecha;
    st.hora = '';
    pintarCalendario(widget, st);
    anunciar(widget, e(st, 'ne_loading'));
    const r = await pedir(url(st, ENTRADAS_ENDPOINTS.sesiones, { id: st.id, fecha }), null, MS_CONSULTA);
    if (st.fecha !== fecha) return;            // el visitante ya ha pulsado otro día
    if (!r || !r.ok) {
        anunciar(widget, e(st, r && r.error ? claveDeError(r.error) : 'ne_err_red'), true);
        return;
    }
    anunciar(widget, '');
    st.sesiones = Array.isArray(r.sesiones) ? r.sesiones : [];
    const abiertas = st.sesiones.filter((s) => s.libres !== 0);
    // Una sola sesión con sitio: elegirla por el visitante es un clic que no decide nada.
    if (st.sesiones.length === 1 && abiertas.length === 1) {
        elegirSesion(widget, st, abiertas[0].valor);
        return;
    }
    pintarSesiones(widget, st);
    setPaso(widget, st, 'sesion');
}

function elegirSesion(widget, st, hora) {
    const s = st.sesiones.find((x) => x.valor === hora);
    if (!s || s.libres === 0) return;
    st.hora = hora;
    pintarSesiones(widget, st);
    pintarTipos(widget, st);
    setPaso(widget, st, 'entradas');
}

function cambiarCantidad(widget, st, productId, delta) {
    const n = cantidadPermitida(st.cfg, st.cantidades, productId, (Math.trunc(Number(st.cantidades[productId]) || 0) + delta));
    st.cantidades = ajustarVinculados(st.cfg, Object.assign({}, st.cantidades, { [productId]: n }));
    st.idem = '';                              // otra selección = otra compra
    pintarTipos(widget, st);
    setPaso(widget, st, 'entradas');
}

function leerContacto(widget) {
    const v = (n) => {
        const el = widget.querySelector(`[name="${n}"]`);
        return el ? String(el.value || '').trim() : '';
    };
    return { nombre: v('nombre'), telefono: v('telefono'), email: v('email'), _hp: v('_hp') };
}

async function comprar(widget, st) {
    if (esEditor() || st.enviando) return;
    const datos = leerContacto(widget);
    if (!contactoValido(datos.nombre, datos.telefono, datos.email)) {
        anunciar(widget, e(st, 'ne_err_datos'), true);
        const primero = widget.querySelector('.ne-input:invalid') || widget.querySelector('.ne-input');
        if (primero) primero.focus();
        return;
    }
    const c = comprobarSeleccion(st);
    if (!c.lineas.length || !c.cabe) { setPaso(widget, st, 'entradas'); return; }
    if (!st.idem) st.idem = nuevaIdem();
    st.enviando = true;
    setPaso(widget, st, 'datos');
    anunciar(widget, e(st, 'ne_sending'));
    const cuerpo = Object.assign({
        id: st.id, fecha: st.fecha, hora: st.hora, items: itemsDe(c.lineas),
        // El total que ha visto (céntimos): si el servidor calcula otro, avisa antes de cobrar (`precio_cambiado`).
        total: totalCentimos(c.lineas),
        pagina: location.pathname, _idem: st.idem, lang: st.lang,
    }, datos);
    const medio = medioElegido(st);
    if (medio) cuerpo.medio = String(medio.id);
    if (st.agente) {
        cuerpo.agente = st.agente;
        if (pagaLuego(st, totalCentimos(c.lineas))) cuerpo.pagar_luego = true;
    }
    let r;
    try {
        r = await pedir(url(st, ENTRADAS_ENDPOINTS.comprar), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuerpo),
        }, MS_COMPRA);
    } finally {
        st.enviando = false;
    }
    if (r && r.ok && r.pago && r.pago.url) {
        try { sessionStorage.setItem(CLAVE_PAGO, st.id); } catch (_) { /* sin almacenamiento: contesta el primer widget */ }
        anunciar(widget, e(st, 'ne_to_payment'));
        location.href = r.pago.url;
        return;
    }
    if (r && r.ok) {
        st.idem = '';
        anunciar(widget, '');
        pintarHecho(widget, st, r.reserva);
        setPaso(widget, st, 'hecho');
        enfocarHecho(widget);
        return;
    }
    // Error: cada uno devuelve al paso donde se arregla. La clave de idempotencia se conserva si no se sabe
    // qué pasó (red) y con `en_curso` (la primera petición sigue en marcha en el servidor): reintentar con la
    // MISMA devuelve su resultado; con otra, crearía una segunda venta.
    const codigo = r && r.error;
    if (codigo && codigo !== 'en_curso') st.idem = '';
    if (codigo === 'agente_no_valido') {     // lo han desactivado o cambiado entre medias: se vuelve a pedir
        st.agente = '';
        st.pagoOpcional = false;
        st.pagarLuego = false;
        st.agenteAbierto = true;
    }
    anunciar(widget, e(st, claveDeError(codigo)), true);
    setPaso(widget, st, 'datos');
    if (codigo === 'precio_cambiado') {
        // Los precios se rehacen pidiendo otra vez las sesiones del día; la sesión elegida se conserva.
        const hora = st.hora;
        await elegirDia(widget, st, st.fecha);
        if (st.sesiones.some((x) => x.valor === hora)) elegirSesion(widget, st, hora);
        anunciar(widget, e(st, claveDeError(codigo)), true);
    } else if (codigo === 'aforo_completo' || codigo === 'hora_no_disponible') {
        await elegirDia(widget, st, st.fecha);
        anunciar(widget, e(st, claveDeError(codigo)), true);
    } else if (codigo === 'fecha_no_disponible') {
        st.fecha = '';
        pintarCalendario(widget, st);
        setPaso(widget, st, 'dia');
        anunciar(widget, e(st, claveDeError(codigo)), true);
    } else if (codigo === 'entrada_no_valida' || codigo === 'sin_entradas') {
        setPaso(widget, st, 'entradas');
        anunciar(widget, e(st, claveDeError(codigo)), true);
    }
}

function enfocarHecho(widget) {
    const hecho = widget.querySelector('.ne-hecho');
    // Sin desplazar: de llevar la vista al widget se encarga llevarAlWidget (centrado, no pegado arriba
    // bajo la cabecera fija).
    if (hecho && typeof hecho.focus === 'function') {
        try { hecho.focus({ preventScroll: true }); } catch (_) { hecho.focus(); }
    }
}

function quiereMenosMovimiento() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (_) { return false; }
}

/** ¿Se ve al menos la mitad del widget? */
function aLaVista(widget) {
    if (typeof widget.getBoundingClientRect !== 'function' || typeof window === 'undefined') return true;
    const r = widget.getBoundingClientRect();
    const alto = window.innerHeight || 0;
    const visible = Math.min(r.bottom, alto) - Math.max(r.top, 0);
    return visible >= Math.min(r.height, alto) / 2;
}

/**
 * A la vuelta del banco la página carga arriba del todo y el resultado queda donde el comprador no
 * mira: se lleva la vista al widget (centrado: la cabecera fija tapa el borde de arriba) y se resalta
 * un momento. Se repite al llegar el veredicto si las fotos de encima lo han movido fuera de la vista.
 */
function llevarAlWidget(widget, resaltar) {
    if (!aLaVista(widget) && typeof widget.scrollIntoView === 'function') {
        try { widget.scrollIntoView({ behavior: quiereMenosMovimiento() ? 'auto' : 'smooth', block: 'center' }); } catch (_) { widget.scrollIntoView(); }
    }
    if (!resaltar) return;
    widget.classList.remove('ne-resaltado');
    void widget.offsetWidth;   // reinicia la animación si ya estaba puesta
    widget.classList.add('ne-resaltado');
    setTimeout(() => widget.classList.remove('ne-resaltado'), 2600);
}

function reiniciar(widget, st) {
    clearTimeout(st.sondeo);
    st.fecha = '';
    st.hora = '';
    st.sesiones = [];
    st.cantidades = {};
    st.idem = '';
    widget.querySelectorAll('.ne-input').forEach((i) => { if (i.name === '_hp') i.value = ''; });
    pintarCalendario(widget, st);
    anunciar(widget, '');
    setPaso(widget, st, 'dia');
}

/** A la vuelta del banco: pregunta por la transacción hasta que se resuelve o se acaba el sondeo. */
function atenderVueltaPago(widget, st, vuelta) {
    pintarHecho(widget, st, null, 'esperando');
    setPaso(widget, st, 'hecho');
    llevarAlWidget(widget, true);
    try {
        history.replaceState(history.state, '', location.pathname + quitarVueltaPago(location.search) + location.hash);
    } catch (_) { /* sin History API: recargar repetiría la consulta, que es inocua */ }
    let intentos = 0;
    const preguntar = async () => {
        intentos += 1;
        const r = await pedir(url(st, ENTRADAS_ENDPOINTS.estado + vuelta.tx + '/'), null, MS_CONSULTA);
        const v = veredictoPago(r);
        if (v === 'ok') {
            pintarHecho(widget, st, r.resultado.reserva);
            enfocarHecho(widget);
            llevarAlWidget(widget, true);
            return;
        }
        // No cobrada, caducada o devuelta → definitivo. En el banco o con la venta creándose, se
        // espera al aviso; un cupo agotado (`rate_limited`) tampoco es un no: puede estar cobrado.
        // Canceló en la pasarela y el servidor no dice que cobrara: es un «no se completó», no un «esperando al
        // banco» (no hay aviso que esperar). Una tx cobrada da 'ok' arriba y gana a la marca.
        if (v === 'error' || (v === 'esperar' && vuelta.cancelado && r && r.ok && !r.materializada
                && ['enviada', 'creada'].indexOf(r.estado) >= 0)) {
            pintarHecho(widget, st, null, 'error');
            enfocarHecho(widget);
            llevarAlWidget(widget, true);
            return;
        }
        if (intentos >= SONDEO_INTENTOS) {
            pintarHecho(widget, st, null, 'pendiente');
            enfocarHecho(widget);
            llevarAlWidget(widget, false);
            return;
        }
        st.sondeo = setTimeout(preguntar, SONDEO_MS);
    };
    preguntar();
}

// ── Hidratación ──────────────────────────────────────────────────────────────
/** El widget que contesta a la vuelta del banco: el del evento que se pagaba, o el primero. */
function widgetDeLaVuelta(widgets) {
    let id = '';
    try { id = sessionStorage.getItem(CLAVE_PAGO) || ''; sessionStorage.removeItem(CLAVE_PAGO); } catch (_) { /* sin almacenamiento */ }
    return widgets.find((w) => String(w.getAttribute('data-n3i-entradas')).trim() === id) || widgets[0];
}

/**
 * Pinta los widgets que aún no lo estén. Idempotente: la marca va en el DOM (`data-ne-ready`), así
 * que un re-render del editor (elemento nuevo, sin marca) se vuelve a pintar solo.
 */
export function hydrateEntradas() {
    if (typeof document === 'undefined') return;
    const nuevos = Array.prototype.slice.call(document.querySelectorAll(`${SELECTOR}:not([data-ne-ready])`));
    if (!nuevos.length) return;
    const vuelta = (!esEditor() && typeof location !== 'undefined') ? leerVueltaPago(location.search) : null;
    const quien = vuelta ? widgetDeLaVuelta(nuevos) : null;
    nuevos.forEach((w) => {
        w.setAttribute('data-ne-ready', '1');
        const st = estadoDe(w);
        cargarEvento(w, st).then((ok) => {
            if (ok && w === quien) atenderVueltaPago(w, st, vuelta);
        });
    });
}

function widgetDe(el) {
    return el && el.closest ? el.closest(SELECTOR) : null;
}

/** Instala la delegación del widget. Idempotente (guard global); corre en editor y publicado. */
export function initEntradas() {
    if (typeof window === 'undefined' || window.__n3iEntradas) return;
    window.__n3iEntradas = true;

    document.addEventListener('click', (ev) => {
        const w = widgetDe(ev.target);
        if (!w) return;
        const st = estadoDe(w);
        if (!st.cfg) return;
        const objetivo = (sel) => {
            const el = ev.target.closest(sel);
            return el && w.contains(el) ? el : null;
        };

        const nav = objetivo('.ne-cal-nav');
        if (nav) {
            if (nav.disabled) return;
            const [a, m] = nav.getAttribute('data-mes').split('-').map(Number);
            if (!mesNavegable(a, m, st.cfg)) return;
            st.mes = [a, m];
            pintarCalendario(w, st);
            return;
        }
        const dia = objetivo('.ne-dia');
        if (dia) { if (!dia.disabled) elegirDia(w, st, dia.getAttribute('data-fecha')); return; }
        const sesion = objetivo('.ne-sesion');
        if (sesion) { if (!sesion.disabled) elegirSesion(w, st, sesion.getAttribute('data-hora')); return; }
        const mas = objetivo('.ne-mas');
        const menos = objetivo('.ne-menos');
        if (mas || menos) {
            const contador = (mas || menos).closest('.ne-contador');
            const pid = contador ? contador.getAttribute('data-producto') : '';
            if (pid) cambiarCantidad(w, st, pid, mas ? 1 : -1);
            return;
        }
        if (objetivo('.ne-seguir')) {
            const c = comprobarSeleccion(st);
            if (c.lineas.length && c.cabe) {
                anunciar(w, '');
                setPaso(w, st, 'datos');
                const primero = w.querySelector('.ne-input');
                if (primero) primero.focus();
            }
            return;
        }
        if (objetivo('.ne-volver')) {
            const i = PASOS.indexOf(st.paso);
            // Al volver desde las entradas, si el día solo tenía una sesión, se salta su paso:
            // ese paso nunca se enseñó.
            let destino = PASOS[Math.max(0, i - 1)];
            if (destino === 'sesion' && st.sesiones.length === 1) destino = 'dia';
            anunciar(w, '');
            setPaso(w, st, destino);
            return;
        }
        if (objetivo('.ne-agente-abrir')) {
            st.agenteAbierto = true;
            pintarResumen(w, st);
            const input = w.querySelector('.ne-agente-input');
            if (input) input.focus();
            return;
        }
        if (objetivo('.ne-agente-aplicar')) { aplicarAgente(w, st); return; }
        if (objetivo('.ne-agente-quitar')) { quitarAgente(w, st); return; }
        const cuando = objetivo('.ne-cuando-opcion');
        if (cuando) { elegirCuando(w, st, cuando.getAttribute('data-cuando')); return; }
        const medio = objetivo('.ne-medio');
        if (medio) { elegirMedio(w, st, medio.getAttribute('data-medio'), false); return; }
        if (objetivo('.ne-comprar')) { comprar(w, st); return; }
        if (objetivo('.ne-otra')) { reiniciar(w, st); }
    });

    // Enter en un campo de los datos = comprar (no hay <form>: el saneo y el editor lo complican).
    document.addEventListener('keydown', (ev) => {
        // Flechas dentro de «¿Cómo quieres pagar?»: como un grupo de radios (solo el elegido está en el Tab).
        if (ev.target.classList && ev.target.classList.contains('ne-medio')
            && ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].indexOf(ev.key) !== -1) {
            const wm = widgetDe(ev.target);
            if (!wm) return;
            const botones = Array.prototype.slice.call(wm.querySelectorAll('.ne-medio'));
            const i = botones.indexOf(ev.target);
            const paso = (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') ? 1 : -1;
            const sig = botones[(i + paso + botones.length) % botones.length];
            ev.preventDefault();
            if (sig) elegirMedio(wm, estadoDe(wm), sig.getAttribute('data-medio'), true);
            return;
        }
        // Flechas en «¿Cuándo quieres pagar?»: dos opciones, como un grupo de radios.
        if (ev.target.classList && ev.target.classList.contains('ne-cuando-opcion')
            && ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].indexOf(ev.key) !== -1) {
            const wc = widgetDe(ev.target);
            if (!wc) return;
            ev.preventDefault();
            const stc = estadoDe(wc);
            elegirCuando(wc, stc, stc.pagarLuego ? 'ahora' : 'luego');
            return;
        }
        if (ev.key !== 'Enter' || !ev.target.classList || !ev.target.classList.contains('ne-input')) return;
        const w = widgetDe(ev.target);
        if (!w) return;
        ev.preventDefault();
        // Enter en la casilla del código la aplica; en los demás campos, compra.
        if (ev.target.classList.contains('ne-agente-input')) { aplicarAgente(w, estadoDe(w)); return; }
        comprar(w, estadoDe(w));
    });

    hydrateEntradas();
}
